/**
 * License API client (public endpoints — no auth token required).
 *
 * Backs the LicenseGuard / ActivationScreen. A fresh install is locked at the
 * API layer; these calls let the operator read the Install ID and submit a
 * vendor-issued activation code.
 */

import { API_BASE_URL } from './api';

export interface LicenseStatus {
  install_id: string | null;
  enforced: boolean;
  activated: boolean;
  locked: boolean;
  customer: string | null;
  expires_at: string | null;
  reason: string;
}

export interface ActivateResult {
  ok: boolean;
  message: string;
  customer?: string | null;
  expires_at?: string | null;
}

/** GET /license/status — current install identity + lock state. */
export async function getLicenseStatus(): Promise<LicenseStatus> {
  const res = await fetch(`${API_BASE_URL}/license/status`, {
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`license status ${res.status}`);
  }
  const json = await res.json();
  return (json?.data ?? json) as LicenseStatus;
}

/** POST /license/activate — submit a vendor activation code. */
export async function activateLicense(token: string): Promise<ActivateResult> {
  const res = await fetch(`${API_BASE_URL}/license/activate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: token.trim() }),
  });

  const json = await res.json().catch(() => ({} as any));

  if (!res.ok) {
    const message =
      json?.error?.details ||
      json?.error?.message ||
      json?.message ||
      'Activation failed. Check the code and try again.';
    return { ok: false, message };
  }

  const d = json?.data ?? json;
  return {
    ok: true,
    message: d?.message || 'Activation successful.',
    customer: d?.customer ?? null,
    expires_at: d?.expires_at ?? null,
  };
}
